import { logger } from '@vagabondinternet/logging';
import {
  REGISTER_MESSAGES,
  RegisterScreen,
  type RegistrationDetails,
  type RegistrationErrors,
} from '@words-happen/screens';
import type { SocialProvider } from '@words-happen/ui-components';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AUTH_MESSAGES,
  type AuthFailure,
  authErrorCode,
  authFailure,
  browserIdentitySession,
  browserSignInLinkEmailStore,
  type EmailLinkSignIn,
  type IdentitySession,
  identityPlatformEmailLink,
  registerWithEmail as identityPlatformRegister,
  identityPlatformSocialSignIn,
  type RegisterWithEmail,
  type SignInLinkEmailStore,
  type SocialSignIn,
  sharedAuthMessage,
} from '../../api';
import { ENABLED_AUTH_PROVIDERS } from '../../config/auth-providers';
import { type BrowserNavigation, routeEmail, useAuthNavigation } from '../../navigation';
import { ROUTES } from '../../routes';
import { HandoffScreen, useApiWarmUp, useSessionHandoff } from '../../session';
import { SignInLinkSentScreen, useSignInLinkRequest } from '../sign-in-link';
import { useSocialSignIn } from '../use-social-sign-in';
import { AccountExistsScreen } from './account-exists-screen';

export const REGISTER_FAILED = "We couldn't create your account. Check your details and try again.";

/** The form alert for a failed registration that has no screen or field error of its own. */
export const registerErrorMessage = (failure: AuthFailure): string =>
  failure === 'not-allowed' ? AUTH_MESSAGES.registrationNotOpen : (sharedAuthMessage(failure) ?? REGISTER_FAILED);

export interface RegisterPageProps {
  registerWithEmail?: RegisterWithEmail;
  /** Social sign-in with a redirect, which creates the account if need be. Defaults to Identity Platform's. */
  socialSignIn?: SocialSignIn;
  /** The social providers that can be used; the others are shown "coming soon". Defaults to VITE_AUTH_PROVIDERS. */
  authProviders?: readonly SocialProvider[];
  /** Firebase's signed-in user and the API's session endpoints, for a social sign-up. Defaults to the browser's. */
  session?: IdentitySession;
  /** Email link sign-in, offered for an address that already has an account. Defaults to Identity Platform's. */
  emailLink?: EmailLinkSignIn;
  /** Where this device remembers the address a sign-in link went to. Defaults to localStorage. */
  emailStore?: SignInLinkEmailStore;
  /** How the back chevron leaves the app. Defaults to the window's history and location. */
  navigation?: BrowserNavigation;
}

/**
 * /register: email registration with Identity Platform for RegisterScreen. A new account stays signed in, is sent a
 * verification email, and the player goes to /verify-email with the address in history state (never the URL); an
 * address that already has an account gets its own screen, offering a sign-in link to it (then "Check your email"),
 * and whose "Forgot password?" goes to /forgot-password with the address. An address handed over in history state, e.g.
 * from a failed sign-in, prefills the form.
 *
 * A social provider's button leaves for its sign-in page, which creates the account if there isn't one, and comes back
 * here: the redirect is completed and the player handed off to return_to or GAME_HOME_URL, as on /sign-in. The account
 * takes the provider's display name. An unverified player is sent the verification email and goes to /verify-email.
 *
 * On loading, the page warms the API up (session.warmUpApi), so a cold API starts while the player registers.
 */
export const RegisterPage: React.FC<RegisterPageProps> = ({
  registerWithEmail = identityPlatformRegister,
  socialSignIn = identityPlatformSocialSignIn,
  authProviders = ENABLED_AUTH_PROVIDERS,
  session = browserIdentitySession,
  emailLink = identityPlatformEmailLink,
  emailStore = browserSignInLinkEmailStore,
  navigation,
}) => {
  const { switchTo, back, leave, returnTo, continueUrl, continueUrlTo } = useAuthNavigation(navigation);
  const { state } = useLocation();
  const [defaultEmail] = useState(() => routeEmail(state));
  const signInLink = useSignInLinkRequest({ emailLink, emailStore, continueUrl: continueUrlTo(ROUTES.signInLink) });
  const toVerifyEmail = useCallback(
    (verificationSent?: boolean) =>
      switchTo(ROUTES.verifyEmail, {
        redirected: true,
        ...(verificationSent === undefined ? {} : { verificationSent }),
      }),
    [switchTo],
  );
  const redirectToVerifyEmail = useCallback(() => toVerifyEmail(), [toVerifyEmail]);
  const social = useSocialSignIn({
    socialSignIn,
    enabled: authProviders,
    notAllowedMessage: AUTH_MESSAGES.registrationNotOpen,
    continueUrl,
    // Runs after mount, once the handoff below exists.
    onSignedIn: (result) => (result.verified ? handoff.complete() : toVerifyEmail(result.verificationSent)),
  });
  const handoff = useSessionHandoff({ session, returnTo, leave, onUnverified: redirectToVerifyEmail });
  useApiWarmUp(session);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<RegistrationErrors>();
  const [existingEmail, setExistingEmail] = useState<string>();

  const fail = (email: string, cause: unknown) => {
    const failure = authFailure(cause);
    logger.error({ message: 'Email registration failed', code: authErrorCode(cause) });
    if (failure === 'account-exists') {
      setExistingEmail(email);
    } else if (failure === 'password-policy') {
      setFieldErrors({ password: REGISTER_MESSAGES.passwordPolicy });
    } else {
      setError(registerErrorMessage(failure));
    }
  };

  const submit = async (details: RegistrationDetails) => {
    setError(undefined);
    social.clearError();
    setFieldErrors(undefined);
    setSubmitting(true);
    try {
      const { verificationSent } = await registerWithEmail(details, { continueUrl });
      switchTo(ROUTES.verifyEmail, { email: details.email, verificationSent });
    } catch (cause) {
      fail(details.email, cause);
      setSubmitting(false);
    }
  };

  /** To /sign-in, prefilled with the address the player registered with when there is one. */
  const signIn = (email?: string) => switchTo(ROUTES.signIn, email ? { email } : undefined);

  if (handoff.state.phase !== 'idle' || social.finishing) {
    return <HandoffScreen handoff={handoff} />;
  }

  return signInLink.sentTo ? (
    <SignInLinkSentScreen
      email={signInLink.sentTo}
      {...signInLink.sentScreen}
      onBack={() => signIn(signInLink.sentTo)}
    />
  ) : existingEmail ? (
    <AccountExistsScreen
      email={existingEmail}
      signInLink={signInLink.resend}
      onSendSignInLink={() => void signInLink.request(existingEmail)}
      onSignIn={() => signIn(existingEmail)}
      onForgotPassword={() => switchTo(ROUTES.forgotPassword, { email: existingEmail })}
    />
  ) : (
    <RegisterScreen
      submitting={submitting}
      error={error ?? social.error}
      fieldErrors={fieldErrors}
      defaultEmail={defaultEmail}
      onSubmit={submit}
      enabledProviders={authProviders}
      busyProvider={social.busyProvider}
      onSelectProvider={social.select}
      onSignIn={() => signIn()}
      onBack={back}
    />
  );
};
